import type {
  ExtendedColumnFilter,
  FilterOperator,
  FilterVariant
} from '@/types/data-table';
import type { ColumnPinningState, RowData } from '@tanstack/react-table';
import type { Column } from '@/lib/table-features';

import { dataTableConfig } from '@/config/data-table';

/**
 * Migrates a persisted v8 `{ left, right }` columnPinning blob (and hardens
 * against a malformed/partial one) to v9's `{ start, end }` shape.
 *
 * Pre-migration `localStorage` entries (and any hand-authored `initialState`)
 * are shaped like v8's `{ left: [...], right: [...] }`. TanStack v9's
 * column-pinning feature reads `state.columnPinning.start`/`.end` directly and
 * calls `.includes(...)` on them unconditionally, so a `{ left, right }` blob
 * (or one missing a side) throws `TypeError: undefined is not an object
 * (evaluating 'end.includes')` the moment a non-start-pinned column is
 * checked. Always route persisted/initial columnPinning through this before
 * it reaches `useTable`'s `state`.
 */
export function normalizeColumnPinning(value: unknown): ColumnPinningState {
  const v = (value ?? {}) as Partial<ColumnPinningState> & {
    left?: string[];
    right?: string[];
  };
  return { start: v.start ?? v.left ?? [], end: v.end ?? v.right ?? [] };
}

export function getCommonPinningStyles<TData extends RowData>({
  column,
  isHeader = false,
  minColWidth,
  isRtl = false
}: {
  column: Column<TData>;
  isHeader?: boolean;
  /** Measured minimum width (e.g. header text width) — column can't shrink below this */
  minColWidth?: number;
  /** RTL layout — swaps sticky CSS properties so pinning works with reversed scroll */
  isRtl?: boolean;
}): React.CSSProperties {
  const isPinned = column.getIsPinned();
  const isLastStartPinnedColumn =
    isPinned === 'start' && column.getIsLastColumn('start');
  const isFirstEndPinnedColumn =
    isPinned === 'end' && column.getIsFirstColumn('end');

  const size = column.getSize();
  const effectiveMin = minColWidth ? Math.max(size, minColWidth) : size;

  // In RTL, scroll direction is reversed so sticky `left`/`right` must swap.
  // TanStack 'start'-pinned → CSS `right` in RTL (sticks to start/right edge).
  // TanStack 'end'-pinned → CSS `left` in RTL (sticks to end/left edge).
  // The DataTableColumnHeader swaps the pin() call so "Pin Left" → pin('end')
  // in RTL, making the double-swap transparent: user clicks "Pin Left" → physical left.
  const leftProp = isRtl ? 'right' : 'left';
  const rightProp = isRtl ? 'left' : 'right';
  const leftShadow = isRtl
    ? '-4px 0 8px -4px oklch(0 0 0 / 0.08)'
    : '4px 0 8px -4px oklch(0 0 0 / 0.08)';
  const rightShadow = isRtl
    ? '4px 0 8px -4px oklch(0 0 0 / 0.08)'
    : '-4px 0 8px -4px oklch(0 0 0 / 0.08)';

  return {
    boxShadow: isPinned
      ? isLastStartPinnedColumn
        ? leftShadow
        : isFirstEndPinnedColumn
          ? rightShadow
          : undefined
      : undefined,
    [leftProp]:
      isPinned === 'start' ? `${column.getStart('start')}px` : undefined,
    [rightProp]: isPinned === 'end' ? `${column.getAfter('end')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    background: isPinned
      ? isHeader
        ? 'var(--muted)'
        : 'var(--background)'
      : undefined,
    // Only PINNED columns get a hard width lock. Their sticky offset math
    // (getStart/getAfter) needs an exact, stable pixel width to line the
    // sticky cells up. Non-pinned columns are deliberately left unconstrained
    // so the browser's auto table-layout (+ `whitespace-nowrap` on every cell)
    // sizes them to fit their CURRENT content. That is what stops data from
    // being clipped and headers from overlapping when the content, column set
    // or locale changes — a fixed width measured once would go stale the moment
    // the rows change (pagination/filter/sort) and clip the new values.
    ...(isPinned
      ? {
          width: effectiveMin,
          minWidth: effectiveMin,
          maxWidth: effectiveMin
        }
      : {}),
    zIndex: isPinned ? 1 : undefined
  };
}

export function getFilterOperators(filterVariant: FilterVariant) {
  const operatorMap: Record<
    FilterVariant,
    { label: string; value: FilterOperator }[]
  > = {
    text: dataTableConfig.textOperators,
    number: dataTableConfig.numericOperators,
    range: dataTableConfig.numericOperators,
    date: dataTableConfig.dateOperators,
    dateRange: dataTableConfig.dateOperators,
    dateTimeRange: dataTableConfig.dateOperators,
    boolean: dataTableConfig.booleanOperators,
    select: dataTableConfig.selectOperators,
    multiSelect: dataTableConfig.multiSelectOperators,
    infiniteMultiSelect: dataTableConfig.multiSelectOperators
  };

  return operatorMap[filterVariant] ?? dataTableConfig.textOperators;
}

export function getDefaultFilterOperator(filterVariant: FilterVariant) {
  const operators = getFilterOperators(filterVariant);

  return operators[0]?.value ?? (filterVariant === 'text' ? 'iLike' : 'eq');
}

export function getValidFilters<TData>(
  filters: ExtendedColumnFilter<TData>[]
): ExtendedColumnFilter<TData>[] {
  return filters.filter(
    (filter) =>
      filter.operator === 'isEmpty' ||
      filter.operator === 'isNotEmpty' ||
      (Array.isArray(filter.value)
        ? filter.value.length > 0
        : filter.value !== '' &&
          filter.value !== null &&
          filter.value !== undefined)
  );
}
